import addHours from 'date-fns/addHours';
import { extInfo } from 'lib/extInfo';
import { logger } from 'lib/logger';
import { type AiImprovePathType, improveImageApiCall } from 'reactApp/api/aiTools/ImproveImageApiCall';
import { improveImageAttemptsApiCall } from 'reactApp/api/aiTools/ImproveImageAttemptsApiCall';
import { improveImageApiDownloadCall } from 'reactApp/api/aiTools/ImproveImageDownloadApiCall';
import { improveImagePollApiCall } from 'reactApp/api/aiTools/ImproveImagePollApiCall';
import {
    aiImproveApiPollingMaxCount,
    aiPhotoImproveClientSideRestictionCheck,
    aiPhotoImproveFreemiumLimit,
    aiPhotoImprovePaymiumLimit,
} from 'reactApp/appHelpers/featuresHelpers/features/aiPhotoImprove';
import {
    aiRmvBgrndFreemiumLimit,
    aiRmvBgrndNoLimit,
    aiRmvBgrndPaymium,
    aiRmvBgrndPaymiumLimit,
} from 'reactApp/appHelpers/featuresHelpers/features/imageTools';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { ConnectionError } from 'reactApp/errors';
import { is24HoursPassed } from 'reactApp/modules/aiTools/aiTools.helpers';
import {
    addNewBackground,
    aiToolsAttemptsInit,
    aiToolsAttemptsSuccess,
    improveImageError,
    improveImageRequest,
    improveImageResultAction,
    improveImageSave,
    improveImageSuccess,
} from 'reactApp/modules/aiTools/aiTools.module';
import {
    getAiImageImproveAvailableAttempts,
    getAiImageImproveNewImage,
    getAiRmvBgrndAvailableAttempts,
    getAiToolsRmvBgrndStartTime,
    getAiToolsStartTime,
} from 'reactApp/modules/aiTools/aiTools.selectors';
import { type AiImageImproveData, aiImageImproveLocalStorageKey, aiRmvBgrndLocalStorageKey } from 'reactApp/modules/aiTools/aiTools.types';
import { processedFileCache } from 'reactApp/modules/aiTools/helpers/ImageTools.helpers';
import { isPaymiumSelector } from 'reactApp/modules/aiTools/isPaymiumSelector';
import { handleAddNewBackground } from 'reactApp/modules/aiTools/sagas/handleAddNewBackground';
import { handleRemoveBackground } from 'reactApp/modules/aiTools/sagas/removeBackground.saga';
import { getAlbumsItemById } from 'reactApp/modules/albums/albums.selector';
import { AttachesSelectors } from 'reactApp/modules/attaches/attaches.selectors';
import { changeHomeHistory } from 'reactApp/modules/home/home.actions';
import { prepareHomeForAttachesClone } from 'reactApp/modules/modifying/sagas/publish.saga';
import { closePopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { getPublicItemById } from 'reactApp/modules/public/public.selectors';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SNACKBAR_CLOSE_TIMEOUT, SNACKBAR_CLOSE_TIMEOUT_LOW } from 'reactApp/modules/snackbar/snackbar.constants';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { saveDocument } from 'reactApp/modules/uploading/uploading.module';
import { EConflictResolution } from 'reactApp/modules/uploading/uploading.types';
import { closeViewer } from 'reactApp/modules/viewer/viewer.module';
import { getDataFromStorage, saveDataToStorage } from 'reactApp/storage';
import { store as reduxStore } from 'reactApp/store';
import { renderCloneToCloudDialog } from 'reactApp/ui/SelectFolderDialog/SelectFolderDialog.toolkit';
import { sendXray } from 'reactApp/utils/ga';
import { channel } from 'redux-saga';
import { delay, put, select, take, takeLatest } from 'redux-saga/effects';
import { call } from 'typed-redux-saga';

let isAttemptsApiError = false;

export const getTransparentBackgroundFile = () => {};

const POLL_INTERVAL_MS = 5000;

const getTypeFromStorage = (storage?: EStorageType | null): AiImprovePathType => {
    if (storage === EStorageType.public) {
        return 'public';
    }
    return 'file';
};

function* pollImageStatusAPi(taskId: string = '') {
    if (!taskId) {
        return;
    }

    for (let i = 0; i < aiImproveApiPollingMaxCount; i++) {
        try {
            const resp = yield* call(improveImagePollApiCall, { task_id: taskId });

            if (resp.status === 200 && resp.data?.status === 'completed') {
                const respDownload = yield* call(improveImageApiDownloadCall, { task_id: taskId });
                return respDownload.data;
            }
        } catch (_) {}
        yield delay(POLL_INTERVAL_MS);
    }

    sendXray('improve-img-status-api-timeout');
}

function* handleImageProcessRequest({ payload }: ReturnType<typeof improveImageRequest>) {
    const { id, storage, ext, type } = payload;

    try {
        if (type === 'removeBackground') {
            const buffer = yield handleRemoveBackground(payload);
            processedFileCache.setTransparent(id, buffer);
            return;
        }

        let path = id;

        if (storage === EStorageType.albums) {
            const item = yield select(getAlbumsItemById, id);

            path = item.path;
        }

        const isAttachesOrStock =
            storage === EStorageType.attaches || storage === EStorageType.viewerAttaches || storage === EStorageType.stock;

        if (isAttachesOrStock) {
            const item = yield select(AttachesSelectors.getAttachById, id);
            const newItem = yield prepareHomeForAttachesClone(item, storage as EStorageType, true);

            if (!newItem) {
                yield put(
                    showSnackbarAction({
                        id: 'imageImproveError',
                        type: SnackbarTypes.failure,
                        text: 'Не удалось улучшить фото',
                        closable: true,
                        autoWidth: true,
                        closeTimeout: SNACKBAR_CLOSE_TIMEOUT_LOW,
                    })
                );
                yield put(improveImageError(payload));
                return;
            }

            path = newItem.home;
        }

        const resp = yield* call(improveImageApiCall, {
            path,
            storage_type: getTypeFromStorage(storage),
        });

        const mime = extInfo.getContentType(ext);
        let imageBuffer;

        if (resp?.taskId) {
            imageBuffer = yield pollImageStatusAPi(resp.taskId);
        } else if (resp?.image) {
            imageBuffer = resp.image;
        }

        if (imageBuffer) {
            processedFileCache.setTransparent(id, imageBuffer);
            const blob = new Blob([imageBuffer], { type: mime });
            const image = URL.createObjectURL(blob);

            yield put(improveImageSuccess({ ...payload, imageResult: image }));

            yield put(
                showSnackbarAction({
                    id: 'imageImproveSuccess',
                    type: SnackbarTypes.success,
                    text: 'Нейросеть улучшила фото',
                    closable: true,
                    autoWidth: true,
                    closeTimeout: SNACKBAR_CLOSE_TIMEOUT_LOW,
                })
            );

            if (aiPhotoImproveClientSideRestictionCheck || isAttemptsApiError) {
                const startTime = yield select(getAiToolsStartTime);
                const attemptsAvailable = yield select(getAiImageImproveAvailableAttempts);
                saveDataToStorage<AiImageImproveData>(aiImageImproveLocalStorageKey, {
                    startTime,
                    attemptsAvailable,
                });
            }
        } else {
            yield put(
                showSnackbarAction({
                    id: 'imageImproveError',
                    type: SnackbarTypes.failure,
                    text: 'Не удалось улучшить фото',
                    closable: true,
                    autoWidth: true,
                    closeTimeout: SNACKBAR_CLOSE_TIMEOUT_LOW,
                })
            );
            yield put(improveImageError(payload));
        }
    } catch (error: any) {
        logger.error(error);
        yield put(improveImageError(payload));

        let errorType = error?.response?.status || 'unk';
        const textType = type === 'improve' ? 'улучшить фото' : 'удалить фон';

        // eslint-disable-next-line no-irregular-whitespace
        let text = `Не удалось ${textType} - попробуйте позже`;

        if (error?.response?.error === 'CONDITION/LIMITEXCEEDED') {
            // eslint-disable-next-line no-irregular-whitespace
            text = `Не удалось ${textType} - на сегодня попытки исчерпаны`;
            errorType = 'limit';
        }

        if (error instanceof ConnectionError) {
            // eslint-disable-next-line no-irregular-whitespace
            text = `Не удалось ${textType} — нет интернета`;
            errorType = 'noconnect';
        }

        sendXray(`improve-img-error-${errorType}`);

        yield put(
            showSnackbarAction({
                id: 'imageImproveError',
                type: SnackbarTypes.failure,
                text,
                closable: true,
                autoWidth: true,
                closeTimeout: SNACKBAR_CLOSE_TIMEOUT_LOW,
            })
        );
    }
}

function checkAvailableFromLocalstorage(
    startTime: number | undefined,
    attemptsAvailable: number,
    maxAttemptsAvailable: number,
    key: string
) {
    const dataStored = getDataFromStorage<AiImageImproveData>(key);
    startTime = dataStored.startTime;
    attemptsAvailable = dataStored.attemptsAvailable ?? attemptsAvailable;

    if ((startTime && is24HoursPassed(startTime)) || dataStored.maxAttempts !== maxAttemptsAvailable) {
        startTime = 0;
        attemptsAvailable = maxAttemptsAvailable;
        saveDataToStorage<AiImageImproveData>(key, {
            startTime: 0,
            attemptsAvailable,
            maxAttempts: maxAttemptsAvailable,
        });
    }
    return { startTime, attemptsAvailable };
}

function* handleAiToolsAttemptsInit() {
    let startTime = yield select(getAiToolsStartTime);
    const isPaymium = yield select(isPaymiumSelector);
    const maxAttemptsAvailable = isPaymium ? aiPhotoImprovePaymiumLimit : aiPhotoImproveFreemiumLimit;

    try {
        const currentAttemptsAvailable = yield select(getAiImageImproveAvailableAttempts);

        if (startTime && is24HoursPassed(startTime)) {
            startTime = 0;
        }

        // Проверяем только если уже нет доступных попыток и еще не делали ни одной попытки в сессии
        if (!currentAttemptsAvailable && !startTime) {
            let attemptsAvailable = maxAttemptsAvailable;

            if (aiPhotoImproveClientSideRestictionCheck) {
                const ret = checkAvailableFromLocalstorage(
                    startTime,
                    attemptsAvailable,
                    maxAttemptsAvailable,
                    aiImageImproveLocalStorageKey
                );
                startTime = ret.startTime;
                attemptsAvailable = ret.attemptsAvailable;
            } else {
                const resp = yield* call(improveImageAttemptsApiCall);

                if (resp.status === 200) {
                    attemptsAvailable = resp.data?.attempts_available;
                    if (resp.data?.end_time) {
                        startTime = addHours(resp.data?.end_time, -24).valueOf() * 1000;
                    }
                }
            }

            yield put(
                aiToolsAttemptsSuccess({
                    type: 'improve',
                    attemptsAvailable,
                    startTime,
                })
            );
        }
    } catch (error) {
        logger.error(error);

        isAttemptsApiError = true;

        const ret = checkAvailableFromLocalstorage(startTime, maxAttemptsAvailable, maxAttemptsAvailable, aiImageImproveLocalStorageKey);
        startTime = ret.startTime;
        const attemptsAvailable = ret.attemptsAvailable;

        yield put(
            aiToolsAttemptsSuccess({
                type: 'improve',
                attemptsAvailable,
                startTime,
            })
        );
    }

    if (!aiRmvBgrndNoLimit) {
        yield handleRmvBgrndAttemptsInit();
    }
}

function* handleRmvBgrndAttemptsInit() {
    let startTime = yield select(getAiToolsRmvBgrndStartTime);
    const isPaymium = yield select(isPaymiumSelector);
    const maxAttemptsRmvBgrndAvailable = isPaymium || aiRmvBgrndPaymium ? aiRmvBgrndPaymiumLimit : aiRmvBgrndFreemiumLimit;
    const currentRmvBgrndAttemptsAvailable = yield select(getAiRmvBgrndAvailableAttempts);

    if (startTime && is24HoursPassed(startTime)) {
        startTime = 0;
    }

    // Проверяем только если уже нет доступных попыток и еще не делали ни одной попытки в сессии
    if (!currentRmvBgrndAttemptsAvailable && !startTime) {
        let attemptsAvailable = maxAttemptsRmvBgrndAvailable;

        const ret = checkAvailableFromLocalstorage(startTime, attemptsAvailable, maxAttemptsRmvBgrndAvailable, aiRmvBgrndLocalStorageKey);
        startTime = ret.startTime;
        attemptsAvailable = ret.attemptsAvailable;

        yield put(
            aiToolsAttemptsSuccess({
                type: 'removeBackground',
                attemptsAvailable,
                startTime,
            })
        );
    }
}

const showSaveError = () =>
    showSnackbarAction({
        id: 'imageImproveSaveError',
        type: SnackbarTypes.failure,
        text: 'Не удалось сохранить фото',
        closable: true,
        autoWidth: true,
        closeTimeout: SNACKBAR_CLOSE_TIMEOUT_LOW,
    });

const saveOkMessage = 'Изменения сохранены';

function* showSaveOkSnackbarWithLink(path: string, reload = false) {
    const chan = channel();

    yield put(
        showSnackbarAction({
            id: 'imageImproveSaveOk',
            type: SnackbarTypes.success,
            text: saveOkMessage,
            closable: true,
            buttonText: 'Перейти к файлу',
            onButtonClick: () => {
                chan.put(true);
            },
        })
    );

    setTimeout(() => chan.put(false), SNACKBAR_CLOSE_TIMEOUT);

    if (yield take(chan)) {
        if (reload) {
            open(`/home${path}`, '_self');
        } else {
            yield put(changeHomeHistory({ id: path }));
        }
    }
    chan.close();
}

function* handleImageSave({ payload }: ReturnType<typeof improveImageSave>) {
    try {
        const { id, ext, storage, saveAsNew } = payload;

        const newData: ReturnType<typeof getAiImageImproveNewImage> = yield select(getAiImageImproveNewImage, id);

        if (!newData) {
            yield put(showSaveError());

            return;
        }

        let newId = id;

        if (storage === EStorageType.albums) {
            const item = yield select(getAlbumsItemById, id);

            newId = item.path;
        }

        if (storage === EStorageType.viewerAttaches || storage === EStorageType.public) {
            const selector = storage === EStorageType.public ? getPublicItemById : AttachesSelectors.getAttachById;
            // @ts-ignore
            const item = yield select(selector, id);

            const name = item?.name;

            const destinationChannel = channel();

            renderCloneToCloudDialog({
                items: [],
                isPhone: false,
                isMobile: false,
                selectedId: ROOT_FOLDER_ID,
                autoCreateSelectedId: false,
                isDark: true,
                fullTitle: 'Сохранить в Облако',
                onClose: () => destinationChannel.close(),
                onActionClick: ({ destination }) => destinationChannel.put(destination),
            });

            const selectedDestination = yield take(destinationChannel);

            destinationChannel.close();

            yield closePopupHelper(popupNames.SELECT_FOLDER_DIALOG);

            newId = `${selectedDestination}/${name}`;
        }

        const data = processedFileCache.getNewBackground(id);

        if (!data) {
            yield put(showSaveError());

            return;
        }

        const { result, error } = yield new Promise((resolve) => {
            reduxStore.dispatch(
                saveDocument({
                    home: newId,
                    fileContent: new Uint8Array(data),
                    conflictResolution: saveAsNew ? EConflictResolution.rename : EConflictResolution.rewrite,
                    onResult: resolve,
                })
            );
        });

        if (error || !result) {
            yield put(showSaveError());

            return;
        }

        yield put(improveImageResultAction({ id, storage, result: 'done', ext }));

        if (payload.closeViewer) {
            yield put(closeViewer());
        }

        switch (true) {
            case storage === EStorageType.home && saveAsNew:
                yield put(changeHomeHistory({ id: result }));

                yield put(
                    showSnackbarAction({
                        id: 'imageImproveSaveOk',
                        type: SnackbarTypes.success,
                        text: saveOkMessage,
                        closable: true,
                        autoWidth: true,
                        closeTimeout: SNACKBAR_CLOSE_TIMEOUT_LOW,
                    })
                );
                break;
            case storage !== EStorageType.home && saveAsNew:
                yield showSaveOkSnackbarWithLink(result, storage === EStorageType.public);
                break;
            default:
                yield put(
                    showSnackbarAction({
                        id: 'imageImproveSaveOk',
                        type: SnackbarTypes.success,
                        text: saveOkMessage,
                        closable: true,
                        autoWidth: true,
                        closeTimeout: SNACKBAR_CLOSE_TIMEOUT_LOW,
                    })
                );
                break;
        }
    } catch (error) {
        logger.error(error);
        yield put(showSaveError());
    }
}

// eslint-disable-next-line require-yield
function* handleImageResult({ payload }: ReturnType<typeof improveImageResultAction>) {
    if (payload.result === 'cancel' || payload.result === 'done') {
        processedFileCache.setTransparent(payload.id);
        processedFileCache.setNewBackground(payload.id);
    }
}

export function* watchAiTools() {
    yield takeLatest(improveImageRequest.toString(), handleImageProcessRequest);
    yield takeLatest(aiToolsAttemptsInit.toString(), handleAiToolsAttemptsInit);
    yield takeLatest(improveImageSave.toString(), handleImageSave);
    yield takeLatest(improveImageResultAction.toString(), handleImageResult);
    yield takeLatest(addNewBackground.toString(), handleAddNewBackground);
}
